import { RotateCw } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Button } from './button';
import { Card } from './card';

export interface ErrorCardProps extends Omit<ComponentProps<'div'>, 'title'> {
  /** 무엇이 안 됐는지 한 줄. 예: "회의 목록을 불러오지 못했어요" */
  title: ReactNode;
  /** 사용자 탓이 아닌 이유나 다음에 할 일 한 줄. */
  children?: ReactNode;
  onRetry?: () => void;
  /** @default '다시 시도' */
  retryLabel?: ReactNode;
  /** 재시도 중 — 버튼이 스피너를 보이고 다시 눌리지 않는다. */
  retrying?: boolean;
  /** 선택 보조 링크 하나(예: 상태 확인). */
  secondary?: ReactNode;
}

/**
 * 패널·화면 **전체**가 실패했을 때. 한 줄로 충분하면 `InlineError`다.
 * 그 자리를 비워 두지 않고 무엇이 안 됐는지와 다시 시도를 남긴다(E14 §13).
 */
export function ErrorCard({
  title,
  children,
  onRetry,
  retryLabel = '다시 시도',
  retrying = false,
  secondary,
  className,
  ...props
}: ErrorCardProps) {
  return (
    <Card
      role="alert"
      className={cn('p-card gap-row flex flex-col items-start', className)}
      {...props}
    >
      <div className="gap-tight flex flex-col">
        <p className="text-fg text-sm font-medium">{title}</p>
        {children !== undefined ? <p className="text-ink-muted text-sm">{children}</p> : null}
      </div>
      {onRetry || secondary ? (
        <div className="gap-row flex items-center">
          {onRetry ? (
            <Button
              variant="outline"
              size="sm"
              icon={RotateCw}
              pending={retrying}
              onClick={onRetry}
            >
              {retryLabel}
            </Button>
          ) : null}
          {secondary}
        </div>
      ) : null}
    </Card>
  );
}
